// One decision, and every affordance it does not have.
//
// "Nothing here is editable. There is no add button, no edit affordance, and no delete."
// The design says this is "the one most likely to be quietly violated by someone building
// this" and that "the absence of editing is the product, not an unimplemented feature".
//
// So it is worth saying plainly what would have to be true for an edit control to belong
// here, and that none of it is: there is no write route for a decision — `ROUTES` has
// none, and `FORBIDDEN_PATH_FRAGMENTS` refuses one being added — because the facilitator
// is the only writer of the decision list and people change it by talking. A pencil icon
// on this card would be a control with nothing behind it, and worse, a promise that the
// list is a document rather than a record of what a family agreed.
//
// What replaces it is `DecisionsNote`, which is rendered once above the list and says
// where a correction actually goes. A person who thinks a decision is wrong should find a
// sentence telling them to say so, not spend a minute hunting for a control.
//
// Resolution text is prose and may be long. It is not truncated, not clamped, not put
// behind "show more", and not squeezed into a table cell — "render it as prose rather than
// truncating into a table". The contingency lives in that prose: "the lake house goes to
// Sarah, having given up the apartment for it" is the whole substance of the agreement,
// and a client that showed the first forty characters of it would be showing the half that
// starts a fight.

import type { Decision } from '../api/contract.js';
import { dayOnly } from '../format.js';

export type DecisionCardProps = {
  decision: Decision;
};

/**
 * The states, as words a family would use.
 *
 * `docs/PROTOTYPE-SCOPE.md`: "Each decision carries a state. One state is 'done.' The rest of
 * the state set is not yet determined." So this map is deliberately partial, and anything
 * not in it renders as the server's own word rather than as nothing — an unrecognised
 * state is a state this build has not been taught, not a state that does not exist, and
 * hiding it would make the list stop telling the truth about where things stand.
 */
const STATE_WORDS: Readonly<Record<string, string>> = {
  done: 'Settled',
  open: 'Still open',
  proposed: 'Proposed',
};

/**
 * A checkmark, for the one state (`done`) that gets a filled marker. `currentColor` so it
 * follows `.decision__marker--settled`'s `color: var(--highlight-ink)`.
 */
function CheckIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="0.75rem" height="0.75rem" aria-hidden="true">
      <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function DecisionCard({ decision }: DecisionCardProps): JSX.Element {
  const state = decision.state;
  const settled = state === 'done';
  return (
    <li className="decision">
      {/* No marker at all short of settled: a hollow ring reads as an unchecked checkbox
          or radio button, and nothing on this list is ever pressed (see this file's own
          header comment). `.decision__meta` below already says an unsettled state in
          words, so there is nothing left for an empty circle to add. */}
      {settled ? (
        <span className="decision__marker" aria-hidden="true">
          <CheckIcon />
        </span>
      ) : null}
      <div className="decision__body">
        <h3 className="decision__label">{decision.label}</h3>
        <p className="decision__meta">
          <span className="decision__state">
            {state === null ? 'Nothing decided yet' : (STATE_WORDS[state] ?? state)}
          </span>
          <time className="decision__time" dateTime={decision.updatedAt}>
            {dayOnly(decision.updatedAt)}
          </time>
        </p>
        {decision.resolution === null ? null : (
          <p className="decision__resolution">{decision.resolution}</p>
        )}
      </div>
    </li>
  );
}
